import { getChallanDataFromUlip } from '@services/challanServices'
import { parsePromiseResponse } from '@utils/promiseResponse'
import { formatAmount } from '@utils/amountFormat'
import { formatDateTime } from '@utils/dateFormatChallan'
import { StateCodeMapping } from '@constants/stateCodeStatesMapping'
import { challanViolationMappingJsonSrc, nonVahanStates } from './constants'
import { Challan, RegNumberInfo, SimplifiedChallan } from './interfaces'
import { ChallansStatus } from './enums'

function extractChallanRequiredData(
  challans: Challan[],
  violationNameMapping: Record<string, string>
): SimplifiedChallan[] {
  return challans?.map(challan => ({
    totalAmount: formatAmount(challan?.amount?.total),
    violationName:
      violationNameMapping[challan?.violations?.details?.[0]?.offence || ''] ||
      challan?.violations?.details?.[0]?.offence ||
      'Unknown Violation',
    date: formatDateTime(challan?.violations?.date),
  }))
}

const getViolationNameMapping = async () => {
  let data: Record<string, string> = {}
  try {
    const response = await fetch(challanViolationMappingJsonSrc)
    if (!response.ok) {
      throw new Error(`Failed to fetch violation mapping data: ${response.status}`)
    }
    data = await response.json()
  } catch (error) {
    console.error('Error fetching violation name mapping:', error)
  }
  return data
}

export const getRefactoredChallans = async (regNum: string, envConfig: string) => {
  try {
    const [challanResult, violationResult] = await Promise.allSettled([
      getChallanDataFromUlip(regNum, envConfig),
      getViolationNameMapping(),
    ])
    const challanList = parsePromiseResponse(challanResult)
    const violationNameMapping = parsePromiseResponse(violationResult)

    return extractChallanRequiredData(challanList, violationNameMapping)
  } catch (err) {
    console.error('Encountered Error in getRefactoredChallans', err)
    throw err
  }
}

export const getStateNameFromRegNum = (regNum: string): string => {
  const stateCode = regNum.slice(0, 2).toUpperCase()
  const stateName = StateCodeMapping[stateCode] || ''

  return stateName
}

export const getRegNumberInfo = (regNum: string): RegNumberInfo => {
  const stateName = getStateNameFromRegNum(regNum)
  const isNonVahanState = nonVahanStates.includes(stateName)
  const isNationalVehicle = !stateName
  return { stateName, isNonVahanState, isNationalVehicle }
}

export const getChallanStatus = (numOfChallans: number, isNonVahanState: boolean): ChallansStatus =>
  isNonVahanState ? ChallansStatus.DEFAULT : numOfChallans === 0 ? ChallansStatus.EMPTY : ChallansStatus.SOME
